import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Section, Eyebrow, SectionTitle, currency } from "@/components/Primitives";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Briefcase, Truck, Building2, ShieldCheck, ShieldAlert, ArrowRight, CheckCircle2, Gavel, Percent } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STEPS = ["Requested", "Contractor Assigned", "Materials Delivered", "In Progress", "Inspection", "Payment Released"];
const commissionRate = (b) => (b >= 100000 ? 15 : b >= 25000 ? 20 : 25);

const Dashboard = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [mine, setMine] = useState({ posted: [], connected: [] });
  const [bids, setBids] = useState({});

  useEffect(() => { if (!loading && !user) navigate("/"); }, [user, loading, navigate]);

  const load = () => {
    api.get("/jobs/mine").then(async (r) => {
      setMine(r.data);
      const entries = await Promise.all((r.data.posted || []).map((j) =>
        api.get(`/jobs/${j.id}/bids`).then((b) => [j.id, b.data]).catch(() => null)
      ));
      const map = {};
      for (const e of entries) { if (e) map[e[0]] = e[1]; }
      setBids(map);
    }).catch(() => {});
  };
  useEffect(() => { if (user) load(); }, [user]);

  if (!user) return null;

  const release = async (job) => {
    try { await api.post(`/jobs/${job.id}/release`); const next = Math.min((job.progress_step || 0) + 1, STEPS.length - 1); toast.success(`Milestone approved & released: ${STEPS[next]}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not release"); }
  };
  const fundEscrow = async (job) => {
    try { const { data } = await api.post(`/jobs/${job.id}/fund-escrow`, { lookup_key: "escrow", origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start escrow"); }
  };
  const award = async (jobId, cid) => {
    try { await api.post(`/jobs/${jobId}/award`, { contractor_id: cid }); toast.success("Contractor awarded! They'll pay the platform commission to lock it in."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not award"); }
  };
  const payCommission = async (job) => {
    try { const { data } = await api.post(`/jobs/${job.id}/pay-commission`, { lookup_key: "commission", origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start payment"); }
  };

  const allJobs = [...(mine.posted || []), ...(mine.connected || [])].filter((j, i, a) => a.findIndex((x) => x.id === j.id) === i);

  return (
    <div>
      <div className="eti-grid-bg border-b border-white/10 py-12">
        <Section>
          <Eyebrow>Your Command Center</Eyebrow>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-white">Hi, {user.name?.split(" ")[0]}</h1>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-white/15 text-slate-200 capitalize">{user.role}</Badge>
              {user.role === "contractor" && (user.verification_status === "verified"
                ? <Badge className="bg-emerald-500/15 text-emerald-400 border-0"><ShieldCheck className="h-3 w-3 mr-1" />Verified</Badge>
                : <Badge className="bg-primary/15 text-primary border-0"><ShieldAlert className="h-3 w-3 mr-1" />Unverified</Badge>)}
              {user.subscription?.status === "active" && <Badge className="bg-white/10 text-white border-0 capitalize">{user.subscription.plan?.replace(/_/g, " ")}</Badge>}
            </div>
          </div>
        </Section>
      </div>

      <Section className="py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {[
            [Briefcase, mine.posted?.length || 0, "Jobs Posted"],
            [CheckCircle2, mine.connected?.length || 0, "Leads / Awarded"],
            [Gavel, Object.values(bids).reduce((s, b) => s + (b.bidders?.length || 0), 0), "Bids Received"],
            [Building2, allJobs.filter((j) => j.awarded_to).length, "Awarded Jobs"],
          ].map(([Icon, n, l], i) => (
            <div key={i} className="eti-card rounded-lg p-5" data-testid={`dashboard-stat-${i}`}>
              <Icon className="h-6 w-6 text-primary" />
              <p className="font-display text-3xl font-bold text-white mt-2">{n}</p>
              <p className="text-xs text-slate-400">{l}</p>
            </div>
          ))}
        </div>

        {user.role === "contractor" && user.verification_status !== "verified" && (
          <div className="eti-card rounded-lg p-5 border-primary/40 flex items-start gap-3 mb-10" data-testid="dashboard-verify-banner">
            <ShieldAlert className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div className="flex-1"><p className="font-semibold text-white">Unlock job leads</p><p className="text-sm text-slate-400">Submit your WSIB & insurance to start bidding on customer leads.</p></div>
            <Button size="sm" onClick={() => navigate("/profile")} className="bg-primary hover:bg-primary/90 text-white font-semibold shrink-0">Verify Now</Button>
          </div>
        )}

        <div className="flex items-end justify-between mb-6">
          <SectionTitle>Jobs, Bids & Progress</SectionTitle>
          <Button variant="outline" onClick={() => navigate("/marketplace")} className="border-white/15 text-white" data-testid="dashboard-browse-button">Browse Leads <ArrowRight className="ml-2 h-4 w-4" /></Button>
        </div>

        {allJobs.length === 0 ? (
          <div className="eti-card rounded-lg p-10 text-center"><p className="text-slate-400">No jobs yet. Post a job to receive bids, or bid on a lead in the marketplace.</p></div>
        ) : (
          <div className="space-y-4">
            {allJobs.map((job) => {
              const step = job.progress_step || 0;
              const pct = (step / (STEPS.length - 1)) * 100;
              const isOwner = user.user_id === job.owner;
              const jobBids = bids[job.id];
              return (
                <div key={job.id} className="eti-card rounded-lg p-6" data-testid={`dashboard-job-${job.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <span className="eti-label">{job.trade}</span>
                      <h4 className="font-display font-bold text-white text-lg">{job.title}</h4>
                    </div>
                    <span className="font-mono text-primary font-bold">{currency(job.budget)}</span>
                  </div>

                  {/* Bids (owner) */}
                  {isOwner && jobBids && jobBids.bidders?.length > 0 && (
                    <div className="mt-4 border-t border-white/5 pt-4" data-testid={`dashboard-bids-${job.id}`}>
                      <p className="eti-label mb-2">Bids ({jobBids.bidders.length}/{jobBids.max_bidders})</p>
                      <div className="space-y-2">
                        {jobBids.bidders.map((b) => (
                          <div key={b.user_id} className="flex items-center justify-between gap-3 bg-secondary rounded-lg p-3" data-testid={`bid-${job.id}-${b.user_id}`}>
                            <div className="min-w-0">
                              <Link to={`/profile/${b.user_id}`} className="text-sm text-white font-medium hover:text-primary">{b.name}</Link>
                              {b.proposal && <p className="text-xs text-slate-400 truncate">{b.proposal}</p>}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="font-mono text-primary text-sm">{currency(b.bid_amount)}</span>
                              {job.awarded_to === b.user_id
                                ? <Badge className="bg-emerald-500/15 text-emerald-400 border-0">Awarded</Badge>
                                : !job.awarded_to && <Button size="sm" onClick={() => award(job.id, b.user_id)} className="bg-primary hover:bg-primary/90 text-white" data-testid={`award-${job.id}-${b.user_id}`}>Award</Button>}
                            </div>
                          </div>
                        ))}
                      </div>
                      {job.awarded_to && jobBids && (
                        <p className="mt-3 text-xs font-mono text-slate-400 flex items-center gap-1"><Percent className="h-3 w-3 text-primary" />Platform commission: {Math.round(jobBids.commission_rate * 100)}% ({currency(jobBids.commission_amount)}) {jobBids.commission_paid ? "· paid" : "· awaiting contractor payment"}</p>
                      )}
                    </div>
                  )}
                  {isOwner && (!jobBids || !jobBids.bidders?.length) && !job.awarded_to && (
                    <p className="mt-3 text-xs text-slate-500 font-mono">No bids yet — up to 7 contractors can bid on this lead.</p>
                  )}

                  {/* Awarded contractor: pay commission */}
                  {job.awarded_to === user.user_id && !job.commission_paid && (
                    <div className="mt-4 border-t border-white/5 pt-4">
                      <p className="text-sm text-white font-medium">You won this job!</p>
                      <p className="text-xs text-slate-400 mb-3">Pay the {commissionRate(job.budget)}% platform commission to lock it in.</p>
                      <Button size="sm" onClick={() => payCommission(job)} className="bg-primary hover:bg-primary/90 text-white font-semibold" data-testid={`pay-commission-${job.id}`}>
                        Pay Commission — {currency(job.budget * commissionRate(job.budget) / 100)}
                      </Button>
                    </div>
                  )}
                  {job.awarded_to === user.user_id && job.commission_paid && (
                    <Badge className="mt-4 bg-emerald-500/15 text-emerald-400 border-0"><CheckCircle2 className="h-3 w-3 mr-1" />Commission Paid</Badge>
                  )}

                  {/* Progress */}
                  <div className="mt-5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-white font-medium" data-testid={`progress-tracker-step-${step}`}>{STEPS[step]}</span>
                      <span className="text-xs text-slate-400 font-mono">{step + 1}/{STEPS.length}</span>
                    </div>
                    <Progress value={pct} className="h-2 bg-secondary [&>div]:bg-primary" />
                    <div className="hidden sm:flex justify-between mt-2">
                      {STEPS.map((s, i) => <span key={i} className={`text-[9px] font-mono uppercase ${i <= step ? "text-primary" : "text-slate-600"}`}>{s.split(" ")[0]}</span>)}
                    </div>
                  </div>

                  {/* Escrow */}
                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <Badge className={job.escrow_funded ? "bg-emerald-500/15 text-emerald-400 border-0" : "bg-white/10 text-slate-300 border-0"} data-testid={`escrow-status-${job.id}`}>
                      {job.escrow_funded ? "Escrow Funded" : "Escrow Not Funded"}
                    </Badge>
                    {job.escrow_funded && <span className="text-xs font-mono text-slate-400">Released {currency(job.budget * (step / (STEPS.length - 1)))} / {currency(job.budget)}</span>}
                  </div>
                  {isOwner && !job.escrow_funded && (
                    <Button size="sm" onClick={() => fundEscrow(job)} className="mt-3 bg-primary hover:bg-primary/90 text-white font-semibold" data-testid={`dashboard-fund-escrow-${job.id}`}>Fund Escrow ({currency(job.budget)})</Button>
                  )}
                  {isOwner && job.escrow_funded && step < STEPS.length - 1 && (
                    <Button size="sm" onClick={() => release(job)} className="mt-3 bg-primary hover:bg-primary/90 text-white font-semibold" data-testid={`dashboard-release-${job.id}`}>Approve & Release: {STEPS[step + 1]}</Button>
                  )}
                  {!isOwner && step < STEPS.length - 1 && (
                    <p className="mt-3 text-xs text-slate-400 font-mono">Awaiting homeowner approval to release the next milestone.</p>
                  )}
                  {step >= STEPS.length - 1 && <Badge className="mt-4 bg-emerald-500/15 text-emerald-400 border-0"><CheckCircle2 className="h-3 w-3 mr-1" />Completed & Paid</Badge>}
                </div>
              );
            })}
          </div>
        )}
      </Section>
    </div>
  );
};

export default Dashboard;
